import { TypedChartOfAccountsTemplateUpload } from "@common/helpers/chartOfAccountsTemplateTypeMapping";
import {
    ChartOfAccountsTemplateAccountsBulkSave,
    CLIENT_TEMP_ID_PREFIX,
} from "@common/schemas/chart-of-accounts-template-account.schema";
import { EntityProfileType } from "@common/types/entity";
import {
    appendCreatedRoleAccount,
    CreateRoleAccountValues,
    prefillRolesByDefaultName,
    RoleMappingAccount,
} from "../roles/requiredAccountsMapping";

export interface UploadedRoleAccount extends RoleMappingAccount {
    parentGroupId: string | null;
    sortOrder: number;
}

const toTempId = (id: string) => `${CLIENT_TEMP_ID_PREFIX}${id}`;

export function buildInitialRoleAccounts({
    chart,
    entityLegalType,
}: {
    chart: TypedChartOfAccountsTemplateUpload;
    entityLegalType: EntityProfileType;
}): UploadedRoleAccount[] {
    return prefillRolesByDefaultName({
        accounts: chart.accounts.map((account) => ({
            id: account.id,
            name: account.name,
            type: account.kickType,
            role: null,
            parentGroupId: account.parentGroupId,
            codeText: account.codeText,
            sortOrder: account.sortOrder,
        })),
        entityLegalType,
    });
}

export function addCreatedRoleAccount({
    accounts,
    values,
}: {
    accounts: readonly UploadedRoleAccount[];
    values: CreateRoleAccountValues;
}): UploadedRoleAccount[] {
    const lastSortOrder = Math.max(
        -1,
        ...accounts.map(({ sortOrder }) => sortOrder),
    );
    return appendCreatedRoleAccount({
        accounts,
        role: values.role,
        buildAccount: (id) => ({
            id,
            name: values.name,
            type: values.type,
            role: null,
            parentGroupId: null,
            codeText: values.codeText,
            sortOrder: lastSortOrder + 1,
        }),
    });
}

export function buildUploadedTemplateAccountsPayload({
    chart,
    accounts,
}: {
    chart: TypedChartOfAccountsTemplateUpload;
    accounts: readonly UploadedRoleAccount[];
}): ChartOfAccountsTemplateAccountsBulkSave {
    return {
        groups: chart.groups.map((group) => ({
            id: toTempId(group.id),
            name: group.name,
            type: group.kickType,
            parentGroupId:
                group.parentGroupId === null
                    ? null
                    : toTempId(group.parentGroupId),
            sortOrder: group.sortOrder,
        })),
        accounts: accounts.map((account) => ({
            id: toTempId(account.id),
            codeText: account.codeText,
            kickOriginalCode: null,
            name: account.name,
            type: account.type,
            isDisabled: false,
            isManuallyCreated: true,
            role: account.role,
            variableKind: null,
            parentGroupId:
                account.parentGroupId === null
                    ? null
                    : toTempId(account.parentGroupId),
            sortOrder: account.sortOrder,
        })),
    };
}
